/*
 * Topping tiles for HulkApps Product Options (see assets/bpp-addons.css).
 *
 * The app renders its option list in the browser after the page loads, and can
 * render it again (conditional logic, a variant change). This waits for it and
 * then only ADDS to it:
 *   - a price line on each tile, from the swatch's own data-price, formatted
 *     with the shop's money format (the same one the order summary uses);
 *   - a helper line under each group title ("Optional · +$2.50 each");
 *   - the group's column count, as a CSS variable on .hulkapps_option_value,
 *     from its width (up to four) and tile count so no row ends with a single
 *     tile;
 *   - a name line, only when the app shows no visible name of its own.
 * It never removes, moves or renames an app element, class, id or data
 * attribute, so the app's selection, pricing and cart properties are untouched.
 */
(() => {
  const CONTAINER = '.hulkapps_product_options';
  const GROUP = '.hulkapps_option';
  const TILE = 'label.hulkapps_mswatch_option';
  const SWATCH = '.hulk_po_checkbox';

  // Up to four tiles a row, as many as fit at MIN_TILE px wide in the group's
  // own width (four in the desktop buy column, three on a phone). From that
  // count down, the first that does not leave a single tile on the last row
  // wins; if none does, the last tile spans the row.
  const MAX_COLS = 4;
  const MIN_TILE = 84;
  const GAP = 8; // matches the grid gap in bpp-addons.css

  function columnsFor(list, n) {
    const fit = Math.max(2, Math.min(MAX_COLS, Math.floor((list.clientWidth + GAP) / (MIN_TILE + GAP))));
    const candidates = [];
    for (let c = fit; c >= Math.max(2, fit - 2); c--) candidates.push(c);
    return pickColumns(n, candidates);
  }

  const sized = new WeakSet();
  const resizeObserver =
    'ResizeObserver' in window
      ? new ResizeObserver((entries) => entries.forEach((entry) => layoutList(entry.target)))
      : null;

  function layoutList(list) {
    const n = list.querySelectorAll(TILE).length;
    if (!n || !list.clientWidth) return;
    const { cols, span } = columnsFor(list, n);
    if (list.style.getPropertyValue('--bpp-addon-cols') !== String(cols)) {
      list.style.setProperty('--bpp-addon-cols', String(cols));
    }
    list.toggleAttribute('data-bpp-addon-span', span);
  }

  function pickColumns(n, candidates) {
    for (const c of candidates) {
      if (n <= c || n % c !== 1) return { cols: c, span: false };
    }
    return { cols: candidates[0], span: true };
  }

  function readConfig() {
    try {
      return JSON.parse(document.querySelector('[data-bpp-addons-config]')?.textContent || '{}');
    } catch (error) {
      return {};
    }
  }

  const config = readConfig();

  function formatPrice(amount) {
    const cents = Math.round(parseFloat(amount) * 100);
    if (!Number.isFinite(cents) || cents <= 0) return '';
    const money =
      typeof bppFormatMoney === 'function' ? bppFormatMoney(cents, config.moneyFormat) : (cents / 100).toFixed(2);
    return `+${money}`;
  }

  function isOn(tile) {
    return Boolean(tile.querySelector('input[type="checkbox"]:checked, .swatch_selected'));
  }

  /* Text the app shows on the tile itself, ignoring the tooltip and our lines. */
  function visibleAppText(tile) {
    const walker = document.createTreeWalker(tile, NodeFilter.SHOW_TEXT);
    let text = '';
    while (walker.nextNode()) {
      const node = walker.currentNode;
      const parent = node.parentElement;
      if (!node.textContent.trim() || !parent) continue;
      if (parent.closest('.hulkapps-tooltip, .multiswatch_tooltip_title, .bpp-addons__price, .bpp-addons__name')) continue;
      if (!parent.getClientRects().length) continue;
      text += node.textContent.trim();
    }
    return text;
  }

  function tileName(tile, swatch) {
    return (
      swatch?.dataset.conditionalValue ||
      (tile.getAttribute('aria-label') || '').replace(/\s*\[[^\]]*\]\s*$/, '').trim()
    );
  }

  function enhanceTile(tile) {
    const swatch = tile.querySelector(SWATCH);

    if (!tile.querySelector(':scope > .bpp-addons__name') && !visibleAppText(tile)) {
      const name = tileName(tile, swatch);
      if (name) {
        const span = document.createElement('span');
        span.className = 'bpp-addons__name';
        span.setAttribute('aria-hidden', 'true'); // the tile's aria-label already names it
        span.textContent = name;
        tile.append(span);
      }
    }

    if (!tile.querySelector(':scope > .bpp-addons__price')) {
      const price = formatPrice(swatch?.dataset.price);
      if (price) {
        const span = document.createElement('span');
        span.className = 'bpp-addons__price';
        span.setAttribute('aria-hidden', 'true'); // aria-label carries "[ $2.50 ]"
        span.textContent = price;
        tile.append(span);
      }
    }
  }

  function isRequired(group) {
    return Boolean(
      group.querySelector('[required], .hulkapps-required, .required') ||
        /required/i.test(group.className) ||
        /\*\s*$/.test(group.querySelector('.hulkapps_option_name')?.textContent || '')
    );
  }

  function enhanceGroup(group) {
    const tiles = [...group.querySelectorAll(TILE)];
    if (!tiles.length) return;

    tiles.forEach(enhanceTile);

    const list = group.querySelector('.hulkapps_option_value');
    if (list) {
      layoutList(list);
      if (resizeObserver && !sized.has(list)) {
        sized.add(list);
        resizeObserver.observe(list);
      }
    }

    const title = group.querySelector('.hulkapps_option_name');
    if (title && !title.querySelector('.bpp-addons__help')) {
      const prices = [...new Set(tiles.map((tile) => tile.querySelector(SWATCH)?.dataset.price || ''))];
      const shared = prices.length === 1 ? formatPrice(prices[0]) : '';
      const parts = [];
      if (!isRequired(group) && config.optional) parts.push(config.optional);
      if (shared && config.each) parts.push(config.each.replace('[price]', shared));
      if (parts.length) {
        const help = document.createElement('p');
        help.className = 'bpp-addons__help';
        help.textContent = parts.join(' · ');
        title.append(help);
      }
    }
  }

  function enhance(container) {
    container.querySelectorAll(GROUP).forEach(enhanceGroup);
    if (container.querySelector(TILE)) container.setAttribute('data-bpp-addons-ready', '');
  }

  /*
   * Space / Enter on a focused tile. The app gives each tile tabindex="0" but
   * may not handle the keys; if it has not toggled the topping by keyup, the
   * swatch is clicked the way a pointer would. Checking first means a key the
   * app does handle is never applied twice.
   *
   * The state is read in the CAPTURE phase on window, i.e. before any of the
   * app's own key handlers can run. Read in the bubble phase, a handler of the
   * app's deeper in the tree has already toggled the topping, the "before"
   * state is wrong, and the fallback click toggles it straight back.
   */
  function bindKeyboard(root) {
    const pending = new WeakMap();
    const isToggleKey = (event) => event.key === ' ' || event.key === 'Enter';

    window.addEventListener(
      'keydown',
      (event) => {
        const tile = event.target.closest?.(TILE);
        if (!tile || event.target !== tile || !root.contains(tile) || !isToggleKey(event)) return;
        event.preventDefault(); // Space would scroll the page
        if (!event.repeat) pending.set(tile, isOn(tile));
      },
      true
    );

    root.addEventListener('keyup', (event) => {
      const tile = event.target.closest?.(TILE);
      if (!tile || !isToggleKey(event) || !pending.has(tile)) return;
      const before = pending.get(tile);
      pending.delete(tile);
      setTimeout(() => {
        if (isOn(tile) === before) (tile.querySelector(SWATCH) || tile).click();
      });
    });
  }

  function watch(root) {
    let containerObserver = null;
    let frame = 0;

    const attach = () => {
      const container = root.querySelector(CONTAINER);
      if (!container || !container.querySelector(TILE)) return false;

      enhance(container);
      rootObserver.disconnect();

      // Re-run when the app re-renders its list (conditional options, variant
      // change). enhance() is idempotent, so its own additions settle at once.
      containerObserver?.disconnect();
      containerObserver = new MutationObserver(() => {
        if (!container.isConnected) {
          containerObserver.disconnect();
          rootObserver.observe(root, { childList: true, subtree: true });
          return;
        }
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => enhance(container));
      });
      containerObserver.observe(container, { childList: true, subtree: true });
      return true;
    };

    const rootObserver = new MutationObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(attach);
    });

    if (!attach()) rootObserver.observe(root, { childList: true, subtree: true });

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, () => requestAnimationFrame(attach));
    }
  }

  function init() {
    document.querySelectorAll('.bpp-pdp').forEach((root) => {
      if (root.dataset.bppAddonsBound) return;
      root.dataset.bppAddonsBound = 'true';
      bindKeyboard(root);
      watch(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
